/**
 * Admin Navigation Bar
 * 
 * Simple horizontal navigation for switching between admin sections.
 */

import logger from '../../core/logger.js';

/**
 * Navigation callbacks
 */
export interface AdminNavCallbacks {
  onNavigateToClips: () => void;
  onNavigateToPrompts: () => void;
  onNavigateToModels: () => void;
}

/**
 * Render admin navigation bar
 */
export function renderAdminNav(callbacks: AdminNavCallbacks): HTMLElement {
  logger.info('admin: Rendering admin navigation');
  
  const nav = document.createElement('nav');
  nav.className = 'admin-nav';
  nav.setAttribute('aria-label', 'Admin sections');
  
  const navList = document.createElement('ul');
  navList.className = 'admin-nav-list';
  navList.setAttribute('role', 'tablist');
  
  // Nav items
  const navItems = [
    { label: 'Clips', onClick: callbacks.onNavigateToClips },
    { label: 'Prompts', onClick: callbacks.onNavigateToPrompts },
    { label: 'Models', onClick: callbacks.onNavigateToModels }
  ];
  
  navItems.forEach((item, index) => {
    const li = document.createElement('li');
    li.className = 'admin-nav-item';
    
    const button = document.createElement('button');
    button.textContent = item.label;
    button.className = 'admin-nav-btn';
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-controls', `${item.label.toLowerCase()}-panel`);
    
    // Highlight first item by default
    if (index === 0) {
      button.setAttribute('aria-current', 'page');
    }
    
    button.addEventListener('click', () => {
      // Update active state
      const allBtns = nav.querySelectorAll('.admin-nav-btn');
      allBtns.forEach(btn => {
        btn.removeAttribute('aria-current');
      });
      button.setAttribute('aria-current', 'page');
      
      // Navigate
      item.onClick();
    });
    
    li.appendChild(button);
    navList.appendChild(li);
  });
  
  nav.appendChild(navList);
  return nav;
}

/**
 * Update active nav item
 */
export function updateActiveNavItem(view: 'clips' | 'prompts' | 'models'): void {
  const nav = document.querySelector('.admin-nav');
  if (!nav) return;
  
  const allBtns = nav.querySelectorAll('.admin-nav-btn');
  allBtns.forEach((btn, index) => {
    btn.removeAttribute('aria-current');
    
    // Match button to view
    if (
      (view === 'clips' && index === 0) ||
      (view === 'prompts' && index === 1) ||
      (view === 'models' && index === 2)
    ) {
      btn.setAttribute('aria-current', 'page');
    }
  });
}
